import {
  useDeleteImportedImage,
  useSetSelectionItem,
  useUpdateSelectionOrder,
} from "@/api/section";
import StyledDrawer from "@/components/StyledDrawer";
import { Selection } from "@/lib/cms/section-types";
import { cn } from "@/lib/utils";
import { GripVertical, Pen, Trash } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sortable, SortableItem } from "../../ui/sortable";
import { SectionImportImageForm } from "./SectionImportImageForm";

type SectionSelectedImagesProps = {
  selectedImages: Selection[];
  sectionId: string | undefined;
  sectionName: string;
};

const SectionSelectedImages = ({
  selectedImages,
  sectionId,
  sectionName,
}: SectionSelectedImagesProps) => {
  const { mutateAsync: updateSelectionOrder } =
    useUpdateSelectionOrder(sectionId);

  const onReorder = async (newSelections: Selection[]) => {
    await updateSelectionOrder(newSelections);
  };

  return (
    <div className="flex flex-row flex-wrap gap-6">
      <Sortable
        value={selectedImages.map((s) => {
          return { ...s, id: s.item.id };
        })}
        onValueChange={onReorder}
        orientation="mixed"
      >
        {selectedImages.map((selection) => (
          <SelectionItem
            key={selection.item.id}
            selection={selection}
            sectionId={sectionId}
            sectionName={sectionName}
          />
        ))}
      </Sortable>
    </div>
  );
};

type SelectionItemProps = {
  selection: Selection;
  sectionId: string | undefined;
  sectionName: string;
};

const SelectionItem = ({
  selection,
  sectionId,
  sectionName,
}: SelectionItemProps) => {
  const { t } = useTranslation();
  const { mutateAsync: setSelectionItem } = useSetSelectionItem(sectionId);
  const { mutateAsync: deleteImage } = useDeleteImportedImage(sectionId);
  const [imageHeight, setImageHeight] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const ref = useRef<HTMLImageElement>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleClickTrash = async () => {
    if (selection.item.imageType !== "image") {
      await setSelectionItem({
        itemId: selection.item.id,
        selected: false,
        itemType: selection.item.imageType,
      });
    } else {
      await deleteImage(selection.item.id);
    }
  };

  return (
    <SortableItem
      value={selection.item.id}
      asChild
      asTrigger
      disabled={isModalOpen}
    >
      <div className="relative w-[22%]">
        <img
          src={selection.item.url}
          onClick={() => {}}
          draggable={false}
          ref={ref}
          onLoad={() => setImageHeight(ref.current?.height ?? 0)}
        />
        <div
          className={cn(
            `flex items-center justify-center absolute top-0 right-0 ${isHovered ? "bg-opacity-50" : "bg-opacity-0"} bg-black duration-200 w-full z-10`,
          )}
          style={{ height: imageHeight }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="items-center justify-center">
            <GripVertical size={32} color="#fff" opacity={isHovered ? 1 : 0} />
          </div>
          {selection.item.imageType === "image" && (
            <div
              className="absolute top-3 left-3 border border-zinc-200 bg-zinc-50 rounded-2xl p-1.5 max-w-[90%] overflow-hidden"
              style={{
                opacity: isHovered ? 1 : 0,
                transitionDuration: "0.2s",
              }}
            >
              <p className="text-xs font-medium text-zinc-700">
                {t("cms.selection.section.artworks.createdInCMS.badge")}
              </p>
            </div>
          )}
          <div className="absolute bottom-3 left-3 flex-row gap-2 z-20 pointer-events-auto">
            <StyledDrawer
              className="min-w-[30%]"
              title={t("cms.selection.section.artworks.editImage.title")}
              trigger={
                <button
                  type="button"
                  className="p-1 rounded-md hover:bg-opacity-30 bg-white bg-opacity-0"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => {
                    setIsHovered(false);
                  }}
                >
                  <Pen size={20} color="#fff" opacity={isHovered ? 1 : 0} />
                </button>
              }
              sheetProps={{
                open: isModalOpen,
                onOpenChange: setIsModalOpen,
              }}
            >
              <SectionImportImageForm
                onSuccess={() => setIsModalOpen(false)}
                sectionId={sectionId}
                sectionName={sectionName}
                selection={selection}
                onDelete={handleClickTrash}
              />
            </StyledDrawer>
            <button
              type="button"
              className="p-1 rounded-md hover:bg-opacity-30 bg-white bg-opacity-0"
              onClick={handleClickTrash}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Trash size={20} color="#fff" opacity={isHovered ? 1 : 0} />
            </button>
          </div>
        </div>
      </div>
    </SortableItem>
  );
};

export default SectionSelectedImages;
